interface PdfData {
  formData: {
    company: string
    name: string
    email: string
    phone: string
  }
  pricing: {
    oneTime: { min: number; max: number }
    monthly: { min: number; max: number }
    yearly: { min: number }
  }
  includedFeatures: Array<{ text: string; tooltip: string }>
  allAnswers: Array<{ question: string; answer: string }>
}

export const generateScanPdf = (data: PdfData) => {
  const { formData, pricing, includedFeatures, allAnswers } = data
  const date = new Date().toLocaleDateString('nl-NL', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })

  const featuresHtml = includedFeatures
    .map(
      (f) => `
      <tr>
        <td style="padding: 8px 12px; border-bottom: 1px solid #E5E7EB; font-size: 13px; color: #111827;">
          <span style="color: #007AFF; font-weight: bold; margin-right: 8px;">✓</span>${f.text}
        </td>
      </tr>`,
    )
    .join('')

  const answersHtml = allAnswers
    .map(
      (a, i) => `
      <tr style="background-color: ${i % 2 === 0 ? '#F9FAFB' : '#FFFFFF'};">
        <td style="padding: 10px 12px; border-bottom: 1px solid #E5E7EB; font-size: 12px; color: #6B7280; font-weight: 600; width: 40%; vertical-align: top;">${a.question}</td>
        <td style="padding: 10px 12px; border-bottom: 1px solid #E5E7EB; font-size: 12px; color: #111827;">${a.answer}</td>
      </tr>`,
    )
    .join('')

  const monthlySection =
    pricing.monthly.min > 0
      ? `
      <div style="flex: 1; background: #F9FAFB; border-radius: 8px; padding: 16px; border: 1px solid #E5E7EB;">
        <div style="font-size: 10px; text-transform: uppercase; letter-spacing: 1px; color: #6B7280; font-weight: 700; margin-bottom: 6px;">Maandelijks</div>
        <div style="font-size: 22px; font-weight: 800; color: #111827;">€${pricing.monthly.min} – €${pricing.monthly.max} <span style="font-size: 13px; font-weight: 400; color: #6B7280;">p/m</span></div>
      </div>`
      : ''

  const html = `<!DOCTYPE html>
<html lang="nl">
<head>
  <meta charset="UTF-8">
  <title>Prijsscan – ${formData.company}</title>
  <style>
    @media print {
      body { margin: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .no-print { display: none !important; }
      @page { margin: 15mm 12mm; size: A4; }
    }
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; color: #111827; background: #fff; }
    .page { max-width: 780px; margin: 0 auto; padding: 20px; }
    .header { background: linear-gradient(135deg, #007AFF, #005FCC); color: white; padding: 32px 36px; border-radius: 12px; margin-bottom: 28px; }
    .header h1 { font-size: 28px; font-weight: 800; margin-bottom: 4px; letter-spacing: -0.5px; }
    .header p { font-size: 15px; opacity: 0.85; }
    .meta { display: flex; gap: 24px; flex-wrap: wrap; margin-bottom: 28px; padding: 16px 20px; background: #F9FAFB; border-radius: 8px; border: 1px solid #E5E7EB; }
    .meta-item { font-size: 13px; color: #6B7280; }
    .meta-item strong { color: #111827; display: block; margin-top: 2px; }
    .pricing-grid { display: flex; gap: 12px; margin-bottom: 28px; flex-wrap: wrap; }
    .pricing-grid > div { flex: 1; min-width: 200px; }
    .pricing-main { background: linear-gradient(135deg, #007AFF, #005FCC); color: white; border-radius: 8px; padding: 20px; }
    .pricing-main .label { font-size: 10px; text-transform: uppercase; letter-spacing: 1px; opacity: 0.8; font-weight: 700; margin-bottom: 6px; }
    .pricing-main .amount { font-size: 26px; font-weight: 800; }
    .pricing-main .desc { font-size: 12px; opacity: 0.75; margin-top: 4px; }
    .section-title { font-size: 18px; font-weight: 700; color: #111827; margin-bottom: 12px; padding-bottom: 8px; border-bottom: 2px solid #007AFF; }
    table { width: 100%; border-collapse: collapse; margin-bottom: 28px; }
    .note { background: #FFFBEB; border-left: 4px solid #F59E0B; border-radius: 0 8px 8px 0; padding: 14px 18px; margin-bottom: 28px; font-size: 13px; color: #92400E; }
    .note strong { display: block; margin-bottom: 2px; color: #78350F; }
    .footer { text-align: center; padding-top: 20px; border-top: 1px solid #E5E7EB; font-size: 11px; color: #9CA3AF; margin-top: 12px; }
    .footer a { color: #007AFF; text-decoration: none; }
    .print-btn { display: block; margin: 0 auto 24px; padding: 12px 32px; background: #007AFF; color: white; border: none; border-radius: 8px; font-size: 15px; font-weight: 600; cursor: pointer; }
    .print-btn:hover { background: #005FCC; }
  </style>
</head>
<body>
  <div class="page">
    <button class="print-btn no-print" onclick="window.print()">⬇ Download als PDF</button>

    <div class="header">
      <h1>Prijsscan Resultaat</h1>
      <p>Persoonlijk plan voor ${formData.company}</p>
    </div>

    <div class="meta">
      <div class="meta-item">Naam <strong>${formData.name || '–'}</strong></div>
      <div class="meta-item">E-mail <strong>${formData.email || '–'}</strong></div>
      <div class="meta-item">Telefoon <strong>${formData.phone || 'Niet opgegeven'}</strong></div>
      <div class="meta-item">Datum <strong>${date}</strong></div>
    </div>

    <div class="pricing-grid">
      <div class="pricing-main">
        <div class="label">Eenmalige investering</div>
        <div class="amount">€${pricing.oneTime.min.toLocaleString('nl-NL')} – €${pricing.oneTime.max.toLocaleString('nl-NL')}</div>
        <div class="desc">Websitebouw, design, maatwerk en implementatie</div>
      </div>
      ${monthlySection}
      <div style="flex: 1; background: #F9FAFB; border-radius: 8px; padding: 16px; border: 1px solid #E5E7EB;">
        <div style="font-size: 10px; text-transform: uppercase; letter-spacing: 1px; color: #6B7280; font-weight: 700; margin-bottom: 6px;">Jaarlijks</div>
        <div style="font-size: 22px; font-weight: 800; color: #111827;">Vanaf €${pricing.yearly.min} <span style="font-size: 13px; font-weight: 400; color: #6B7280;">p/j</span></div>
      </div>
    </div>

    <div class="note">
      <strong>Dit is een indicatie</strong>
      In de praktijk werken we deze prijs vaak naar beneden op basis van slimme keuzes en efficiënte werkwijzen.
    </div>

    <h2 class="section-title">Wat is inbegrepen</h2>
    <table>${featuresHtml}</table>

    <h2 class="section-title">Uw antwoorden</h2>
    <table>
      <thead>
        <tr>
          <th style="padding: 10px 12px; text-align: left; font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; color: white; background: #007AFF; border-radius: 6px 0 0 0;">Vraag</th>
          <th style="padding: 10px 12px; text-align: left; font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; color: white; background: #007AFF; border-radius: 0 6px 0 0;">Antwoord</th>
        </tr>
      </thead>
      <tbody>${answersHtml}</tbody>
    </table>

    <div class="footer">
      <p><strong style="color: #111827;">Noord Digital</strong></p>
      <p style="margin-top: 4px;"><a href="https://noorddigital.nl">www.noorddigital.nl</a> · info@noorddigital.nl</p>
      <p style="margin-top: 8px;">Dit document is gegenereerd op ${date}</p>
    </div>
  </div>
</body>
</html>`

  const printWindow = window.open('', '_blank')
  if (printWindow) {
    printWindow.document.write(html)
    printWindow.document.close()
    printWindow.onload = () => {
      setTimeout(() => {
        printWindow.focus()
      }, 250)
    }
  }
}
